import _ from 'lodash';
import React, {
  ReactNode,
  createContext,
  useContext,
  useReducer,
  Dispatch,
} from 'react';
import { v4 as uuid } from 'uuid';
import { useExperiments } from '@wix/fe-essentials-standalone';
import {
  COMMENT_STATUS_CLIENT,
  VIEW_MODE,
  BREAKPOINT_MAX_VALUE,
  EDITOR_TYPE,
  ADD_AUTHORIZATION_HEADERS,
  ANONYMOUS,
} from '@src/utils/constants';
import { getDefaultBreakpoint } from '@src/utils/frameHelpers';

interface AppContextProviderProps {
  children?: ReactNode;
  locale: string;
  comments: any[];
  editorType: keyof typeof EDITOR_TYPE;
  contactId?: string;
  isPartner: boolean;
  accountLogo?: string;
  viewMode?: string;
  siteUrl: string;
  pages: {};
  siteHeaderHeight: number;
  breakpoints?: any;
  user: { isAnonymous: boolean; userName: string; contactId?: string };
  feedbackId: string;
  metaSiteId: string;
  pMetaSiteId: string;
  siteId: string;
  siteName: string;
  ownerId: string;
  feedbackCreatorUserGuid: string;
  error: any;
  experiments: { [specName: string]: boolean };
  linkKey: string;
  ownerName: string;
  instance: string;
}

type State = {
  locale: string;
  errorData: {
    showError: boolean;
    errorMsgLocalKey: string;
  };
  disableTool: boolean;
  comments: any[];
  pageId: string;
  editorType: keyof typeof EDITOR_TYPE;
  contactId?: string;
  isPartner: boolean;
  accountLogo?: string;
  viewMode: string;
  siteUrl: string;
  isSiteResponsive: boolean;
  currentBreakPoint?: any;
  pages: {};
  viewerElement?: any | null;
  biLogger?: any | null;
  siteHeaderHeight: number;
  breakpoints?: any[];
  user: { isAnonymous: boolean; userName: string; contactId?: string };
  feedbackId: string;
  metaSiteId: string;
  pMetaSiteId: string;
  siteId: string;
  siteName: string;
  ownerId: string;
  feedbackCreatorUserGuid: string;
  loading: boolean;
  shouldSchedule: boolean;
  experiments: { [specName: string]: boolean };
  linkKey: string;
  ownerName: string;
  instance: string;
};

type ActionType =
  | { type: ACTIONS.SET_COMMENTS; payload: any[] }
  | { type: ACTIONS.SET_USER_INFO; payload: any }
  | { type: ACTIONS.ADD_COMMENT }
  | { type: ACTIONS.SET_CURRENT_PAGE_ID; payload: string }
  | { type: ACTIONS.DELETE_COMMENT; payload: string }
  | { type: ACTIONS.UPDATE_COMMENT; payload: any }
  | { type: ACTIONS.UPDATE_USER; payload: any }
  | { type: ACTIONS.SET_VIEWER_ELEMENT; payload: any }
  | { type: ACTIONS.SET_BI_LOGGER; payload: any }
  | { type: ACTIONS.CHANGE_VIEW_MODE; payload: string }
  | { type: ACTIONS.SET_CURRENT_BREAKPOINT; payload: any }
  | { type: ACTIONS.DE_ACTIVATE_COMMENTS }
  | { type: ACTIONS.DE_ACTIVATE_COMMENTS_AND_CLEAN_NEW_COMMENTS }
  | { type: ACTIONS.SET_LOADING; payload: boolean }
  | { type: ACTIONS.SCHEDULED_EMAIL_IN_SESSION }
  | { type: ACTIONS.DISABLE_TOOL }
  | {
      type: ACTIONS.SET_ERROR;
      payload: {
        showError: boolean;
        errorMsgLocalKey?: string;
      };
    };
interface AppContextType {
  state: State;
  dispatch: Dispatch<ActionType>;
}

export enum ACTIONS {
  SET_COMMENTS = 'SET_COMMENTS',
  ADD_COMMENT = 'ADD_COMMENT',
  SET_CURRENT_PAGE_ID = 'SET_CURRENT_PAGE_ID',
  DELETE_COMMENT = 'DELETE_COMMENT',
  UPDATE_COMMENT = 'UPDATE_COMMENT',
  UPDATE_USER = 'UPDATE_USER',
  SET_VIEWER_ELEMENT = 'SET_VIEWER_ELEMENT',
  SET_BI_LOGGER = 'SET_BI_LOGGER',
  CHANGE_VIEW_MODE = 'CHANGE_VIEW_MODE',
  SET_CURRENT_BREAKPOINT = 'SET_CURRENT_BREAKPOINT',
  SET_LOADING = 'SET_LOADING',
  SET_USER_INFO = 'SET_USER_INFO',
  DE_ACTIVATE_COMMENTS = 'DE_ACTIVATE_COMMENTS',
  DE_ACTIVATE_COMMENTS_AND_CLEAN_NEW_COMMENTS = 'DE_ACTIVATE_COMMENTS_AND_CLEAN_NEW_COMMENTS',
  SCHEDULED_EMAIL_IN_SESSION = 'SCHEDULED_EMAIL_IN_SESSION',
  SET_ERROR = 'SET_ERROR',
  DISABLE_TOOL = 'DISABLE_TOOL',
}

function getNewCommentPosition(
  currentBreakPoint: any,
  isSiteResponsive: boolean,
  siteHeaderHeight: number,
  element?: any,
  viewMode?: string,
) {
  let centerX;
  if (isSiteResponsive && currentBreakPoint.max !== BREAKPOINT_MAX_VALUE) {
    const innerWidth = element.contentWindow.innerWidth;
    centerX = innerWidth / 2;
  } else if (!isSiteResponsive && viewMode === VIEW_MODE.MOBILE) {
    const { width } = element.contentDocument
      .getElementById('SITE_PAGES')
      .getBoundingClientRect();
    centerX = width / 2;
  } else {
    centerX = window.innerWidth / 2;
  }
  const centerY =
    window.scrollY +
    window.innerHeight / 2 -
    (siteHeaderHeight / 2 + siteHeaderHeight);
  return {
    x: centerX,
    y: centerY,
  };
}

const mainReducer = (state: State, action: ActionType): State => {
  switch (action.type) {
    case ACTIONS.SET_COMMENTS:
      return {
        ...state,
        comments: action.payload,
      };
    case ACTIONS.SET_USER_INFO:
      return {
        ...state,
        user: action.payload,
      };
    case ACTIONS.ADD_COMMENT: {
      const { x, y } = getNewCommentPosition(
        state.currentBreakPoint,
        state.isSiteResponsive,
        state.siteHeaderHeight,
        state.viewerElement,
        state.viewMode,
      );
      return {
        ...state,
        comments: [
          ..._.reject(state.comments, { status: COMMENT_STATUS_CLIENT.NEW }),
          {
            commentLocation: {
              x,
              y,
              yWithScrollY:
                y + state.viewerElement?.contentWindow?.scrollY || 0,
              pageId: state.pageId,
              breakpoint: getDefaultBreakpoint({
                isSiteResponsive: state.isSiteResponsive,
                currentBreakPoint: state.currentBreakPoint,
                viewMode: state.viewMode,
              }),
              innerPath: state.viewerElement?.contentWindow?.location?.pathname,
            },
            status: COMMENT_STATUS_CLIENT.NEW,
            isActive: true,
            createdDate: Date.now(),
            id: uuid(),
          },
        ],
      };
    }
    case ACTIONS.UPDATE_COMMENT: {
      return {
        ...state,
        comments: state.comments.map(comment => {
          if (comment.id === action.payload.id) {
            return {
              ...comment,
              ...action.payload,
              id: action.payload.serverCommentId
                ? action.payload.serverCommentId
                : comment.id,
            };
          }
          return comment;
        }),
      };
    }
    case ACTIONS.UPDATE_USER: {
      return {
        ...state,
        user: action.payload,
      };
    }
    case ACTIONS.DELETE_COMMENT: {
      return {
        ...state,
        comments: state.comments.filter(({ id }) => id !== action.payload),
      };
    }
    case ACTIONS.SET_CURRENT_PAGE_ID: {
      return {
        ...state,
        pageId: action.payload,
      };
    }
    case ACTIONS.CHANGE_VIEW_MODE: {
      return {
        ...state,
        viewMode: action.payload,
        loading: true,
      };
    }
    case ACTIONS.SET_VIEWER_ELEMENT: {
      const { element } = action.payload;
      return {
        ...state,
        viewerElement: element,
      };
    }
    case ACTIONS.SET_BI_LOGGER: {
      return {
        ...state,
        biLogger: action.payload,
      };
    }
    case ACTIONS.SET_CURRENT_BREAKPOINT: {
      return {
        ...state,
        currentBreakPoint: action.payload,
      };
    }
    case ACTIONS.DE_ACTIVATE_COMMENTS_AND_CLEAN_NEW_COMMENTS: {
      const comments: any[] = [];
      state.comments.forEach(comment => {
        if (comment.status === COMMENT_STATUS_CLIENT.NEW) {
          return;
        }
        comments.push({
          ...comment,
          isActive: false,
        });
      });
      return {
        ...state,
        comments,
      };
    }
    case ACTIONS.DE_ACTIVATE_COMMENTS: {
      return {
        ...state,
        comments: state.comments.map(comment => ({
          ...comment,
          isActive: false,
        })),
      };
    }
    case ACTIONS.SET_LOADING: {
      return {
        ...state,
        loading: action.payload,
      };
    }
    case ACTIONS.SET_ERROR: {
      return {
        ...state,
        errorData: { ...state.errorData, ...action.payload },
      };
    }
    case ACTIONS.DISABLE_TOOL: {
      return {
        ...state,
        disableTool: true,
      };
    }
    case ACTIONS.SCHEDULED_EMAIL_IN_SESSION: {
      return {
        ...state,
        shouldSchedule: false,
      };
    }
  }
};

const AppContext = createContext<AppContextType | null>(null);

export function AppContextProvider(props: AppContextProviderProps) {
  const { error } = props;
  const { experiments } = useExperiments({ readOnly: true });

  const INITIAL_STATE: State = {
    ...props,
    loading: true,
    user: experiments.enabled(ADD_AUTHORIZATION_HEADERS)
      ? { isAnonymous: true, userName: '' }
      : {
          ...props.user,
          // should be fixed on the serverless side once we move to the new flow.
          isAnonymous:
            props.user?.isAnonymous || props.user?.userName === ANONYMOUS,
        },
    shouldSchedule: true,
    errorData: {
      showError: false,
      errorMsgLocalKey: 'get_feedback_error',
    },
    disableTool:
      !_.isEmpty(error) ||
      (experiments.enabled(ADD_AUTHORIZATION_HEADERS) && !props.instance),
    pageId: _.get(
      _.find(props.pages, (page: any) => page.isHomePage),
      'id',
    )!,
    isSiteResponsive: props.editorType === EDITOR_TYPE.STUDIO,
    siteHeaderHeight: 96,
    pages: _.reduce(
      props.pages,
      (
        result: any,
        page: {
          id: string;
          isHomePage: boolean;
          staticPage?: any;
          dynamicPage?: any;
        },
      ) => {
        result[page.id] = page.isHomePage
          ? '/'
          : page.staticPage?.uri || page.dynamicPage.prefix;
        return result;
      },
      {},
    ),
    viewMode: VIEW_MODE.DESKTOP,
    currentBreakPoint: {},
  };
  const [state, dispatch] = useReducer(mainReducer, INITIAL_STATE);

  const generateContext = () => {
    return {
      state,
      dispatch,
    };
  };
  return (
    <AppContext.Provider value={generateContext()}>
      {props.children}
    </AppContext.Provider>
  );
}

export function useAppContext() {
  const appContext = useContext(AppContext);
  if (!appContext) {
    throw new Error(
      'useAppContext must be used within the AppContext.Provider',
    );
  }
  return appContext;
}
